import { computed } from '@ember/object';
import { map, mapBy, or } from '@ember/object/computed';
import Service, { inject as service } from '@ember/service';
import { isArray } from '@ember/array';
import { hash, task } from 'ember-concurrency';
import ApiService from './api';
import EnvironmentService from './environment';

const PUSH_PROVIDERS = {
  fcm: {
    provider: 'fcm',
    platform: 'android',
    allowDevelopment: false,
    development: false,
  },
  fcm_ios: {
    provider: 'fcm',
    platform: ['android', 'ios'],
    allowDevelopment: false,
    development: false,
  },
  apns: {
    provider: 'apns',
    platform: 'ios',
    allowDevelopment: true,
    development: false,
  },
  apns_sandbox: {
    provider: 'apns',
    platform: 'ios',
    allowDevelopment: true,
    development: true,
  },
};

const preparePlatform = (key: string, details: Record<string, any>): Record<string, any> => {
  return {
    key,
    ...details,
  };
};

export default class PushService extends Service {
  @service api!: ApiService;
  @service environment!: EnvironmentService;

  credentials = {
    fcm: null,
    apns: null,
  };

  @map('environment.pushProviders', function (provider: keyof typeof PUSH_PROVIDERS) {
    return PUSH_PROVIDERS[provider].provider;
  })
  providers!: string[];

  @computed('environment.pushProviders')
  get platforms() {
    let environmentProviders = this.environment.pushProviders ?? [];

    return environmentProviders.reduce((platforms, provider: keyof typeof PUSH_PROVIDERS) => {
      let details = PUSH_PROVIDERS[provider];

      if (isArray(details.platform)) {
        return [...platforms, ...(details.platform as string[]).map((key) => preparePlatform(key, details))];
      }

      return [...platforms, preparePlatform(details.platform, details)];
    }, []);
  }

  @mapBy('platforms', 'key')
  platformNames!: string[];

  credentialURL(provider: keyof typeof PUSH_PROVIDERS) {
    return `credentials/push_${provider}`;
  }

  @or(
    'loadCredential.isRunning',
    'loadCredentials.isRunning',
    'createOrUpdateCredential.isRunning',
    'updateCredentialConfig.isRunning',
    'disableCredential.isRunning',
  )
  isAnyRequestRunning!: boolean;

  loadCredential = task(async (provider, update = true) => {
    let { credentials } = this;

    try {
      let response = await this.api.request(this.credentialURL(provider), {
        ignoreErrors: true,
        includeStatus: true,
      });

      if (update === true) {
        this.set('credentials', {
          ...credentials,
          [provider]: response.credential,
        });
      }

      return response.credential;
    } catch (e) {
      if (e.status !== '404') {
        return null;
      }

      if (update === true) {
        this.set('credentials', {
          ...credentials,
          [provider]: null,
        });
      }
      return null;
    }
  });

  loadCredentials = task(
    {
      drop: true,
    },
    async () => {
      let credentials = await hash({
        fcm: this.loadCredential.perform('fcm', false),
        apns: this.loadCredential.perform('apns', false),
      });
      this.set('credentials', credentials);
    },
  );

  createOrUpdateCredential = task(
    {
      restartable: true,
    },
    async (provider, data, refresh = true) => {
      await this.api.put(this.credentialURL(provider), {
        data,
      });

      if (refresh === true) {
        await this.environment.reload.perform();
        await this.loadCredential.perform(provider);
      }
    },
  );

  updateCredentialConfig = task(
    {
      restartable: true,
    },
    async (provider, data, refresh = true) => {
      await this.api.put(`${this.credentialURL(provider)}/config`, {
        data,
      });

      if (refresh === true) {
        await this.environment.reload.perform();
        await this.loadCredential.perform(provider);
      }
    },
  );

  disableCredential = task(
    {
      restartable: true,
    },
    async (provider, refresh = true) => {
      await this.api.delete(this.credentialURL(provider));

      if (refresh === true) {
        await this.environment.reload.perform();
        await this.loadCredential.perform(provider);
      }
    },
  );
}

// DO NOT DELETE: this is how TypeScript knows how to look up your services.
declare module '@ember/service' {
  interface Registry {
    push: PushService;
  }
}
